import { format } from "date-fns";
import {
  FileText,
  GitBranch,
  MessageSquare,
  type LucideIcon,
} from "lucide-react";
import { DiffLine, DiffLineType } from "src/components/ui/DiffBlock";
import {
  ChangeManagementChangeType,
  ChangeManagementImpactLevel,
  ChangeManagementOperation,
  ChangeManagementPlan,
} from "src/types/change-management";

export type IndexedOperation = {
  operation: ChangeManagementOperation;
  index: number;
};

export const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

type LabelWithDescription = { label: string; description: string };

const changeTypes: Record<
  NonNullable<ChangeManagementChangeType>,
  LabelWithDescription
> = {
  bugFix: {
    label: "Bug fix",
    description: "Restores the device's originally intended function.",
  },
  patch: {
    label: "Patch",
    description: "Adjusts an existing feature or how it is presented.",
  },
  minor: {
    label: "Minor",
    description: "Adds a feature without changing the device's intended use.",
  },
  major: {
    label: "Major",
    description: "Changes the device's intended use.",
  },
};

export const getChangeTypeDisplay = (
  changeType: ChangeManagementChangeType
): LabelWithDescription =>
  changeType
    ? changeTypes[changeType]
    : {
        label: "Unclassified",
        description:
          "The agent did not have enough evidence to classify this change. See Open issues.",
      };

const impactLevels: Record<
  NonNullable<ChangeManagementImpactLevel>,
  LabelWithDescription
> = {
  low: {
    label: "Low",
    description: "A contained change to one part of the device.",
  },
  medium: {
    label: "Medium",
    description:
      "A change outside the device's core that needs many coordinated updates.",
  },
  high: {
    label: "High",
    description: "A fundamental change to the device's core.",
  },
};

export const getImpactDisplay = (
  level: ChangeManagementImpactLevel
): LabelWithDescription =>
  level
    ? impactLevels[level]
    : {
        label: "Not assessed",
        description:
          "The agent did not have enough evidence about the change's scope to assess its impact.",
      };

export const getSourceIcon = (system: string): LucideIcon => {
  const name = system.toLowerCase();
  if (name === "github") return GitBranch;
  if (name === "chat") return MessageSquare;
  return FileText;
};

export const formatPlanDate = (createdAt: string) =>
  format(new Date(createdAt), "d MMM yyyy");

/** The plan has no title, so use the change it was made for. */
export const getPlanTitle = (plan: ChangeManagementPlan) => {
  const sources = plan.change_documentation_sources;
  const source =
    sources.find(({ system }) => system.toLowerCase() !== "chat") ?? sources[0];
  return source?.refTitle ?? "Change plan";
};

export const countPlanItems = (plan: ChangeManagementPlan) => {
  const operations = plan.recommendations.flatMap(
    ({ operations }) => operations
  );
  const questions = operations.filter(
    ({ type }) => type === "human_review"
  ).length;
  return {
    documents: plan.recommendations.length,
    changes: operations.length - questions,
    questions,
  };
};

export const describePlanCounts = ({
  documents,
  changes,
  questions,
}: ReturnType<typeof countPlanItems>) =>
  `${pluralize(changes, "change")} in ${pluralize(documents, "document")}` +
  (questions ? `, ${pluralize(questions, "question")} for the reviewer` : "");

const operationGroups = {
  replace_text: "body",
  add_text: "body",
  delete_text: "body",
  replace_data_key_answer: "dataKeys",
  add_data_key_answer: "dataKeys",
  delete_data_key_answer: "dataKeys",
  human_review: "reviews",
  create_document: "create",
} as const;

export const groupOperations = (operations: ChangeManagementOperation[]) => {
  const groups: Record<
    (typeof operationGroups)[ChangeManagementOperation["type"]],
    IndexedOperation[]
  > = { body: [], dataKeys: [], reviews: [], create: [] };
  operations.forEach((operation, index) =>
    groups[operationGroups[operation.type]].push({ operation, index })
  );
  return groups;
};

/** Data-key answers are arbitrary JSON; text answers are shown as written. */
export const formatAnswerValue = (value: unknown) => {
  if (value === null || value === undefined) return "(empty)";
  return typeof value === "string" ? value : JSON.stringify(value, null, 2);
};

const toLines = (type: DiffLineType, text: string): DiffLine[] =>
  text
    .replace(/^\n/, "")
    .split("\n")
    .map((line) => ({ type, text: line }));

const hasAnswer = (value: unknown) =>
  value !== null && value !== undefined && value !== "";

export const getOperationDiffLines = (
  operation: ChangeManagementOperation
): DiffLine[] => {
  switch (operation.type) {
    case "replace_text":
      return [
        ...toLines("del", operation.old_text),
        ...toLines("add", operation.new_text),
      ];
    case "add_text": {
      const anchor = toLines("ctx", operation.anchor_text);
      const added = toLines("add", operation.new_text);
      return operation.placement === "before"
        ? [...added, ...anchor]
        : [...anchor, ...added];
    }
    case "delete_text":
      return toLines("del", operation.old_text);
    case "replace_data_key_answer":
      return [
        ...(hasAnswer(operation.old_value)
          ? toLines("del", formatAnswerValue(operation.old_value))
          : []),
        ...toLines("add", formatAnswerValue(operation.new_value)),
      ];
    case "add_data_key_answer":
      return toLines("add", formatAnswerValue(operation.new_value));
    case "delete_data_key_answer":
      return toLines("del", formatAnswerValue(operation.old_value));
    case "human_review":
    case "create_document":
      return [];
  }
};

/** One line that identifies a change while it is collapsed. */
export const getOperationPreview = (operation: ChangeManagementOperation) => {
  switch (operation.type) {
    case "human_review":
      return operation.question;
    case "create_document":
      return operation.template_to_create
        ? `Create from template ${operation.template_to_create}`
        : "Custom document (no template)";
    default: {
      const lines = getOperationDiffLines(operation);
      return (
        lines.find(({ type }) => type === "add") ??
        lines.find(({ type }) => type === "del")
      )?.text;
    }
  }
};

const trimTrailingSlashes = (url: string) => url.replace(/\/+$/, "");

const isWithin = (url: string, base: string) =>
  url === base ||
  (url.startsWith(base) && "/?#".includes(url.charAt(base.length)));

/** Names an evidence link after the plan source it points into, if any. */
export const getEvidenceLabel = (
  sourceUrl: string,
  plan: ChangeManagementPlan
) => {
  const target = trimTrailingSlashes(sourceUrl);
  const source = [
    ...plan.change_documentation_sources.map(({ url, refTitle }) => ({
      url,
      label: refTitle,
    })),
    ...plan.process_sources.map(({ url, title }) => ({ url, label: title })),
  ].find(({ url }) => url && isWithin(target, trimTrailingSlashes(url)));
  if (source) return source.label;
  try {
    const { hostname, pathname } = new URL(sourceUrl);
    return trimTrailingSlashes(`${hostname}${pathname}`);
  } catch {
    return sourceUrl;
  }
};

export type VersionTileTone = "revision" | "routing" | "draft" | "new";
export type VersionTile = { label: string; tone: VersionTileTone };

export const NEW_DOCUMENT_TILE: VersionTile = { label: "New", tone: "new" };

/** `revision` is the number users see, from `getDocVersionRevisionNumber`. */
export const getVersionTile = ({
  revision,
  readyForApproval,
}: {
  revision: number | null;
  readyForApproval: boolean;
}): VersionTile => {
  if (typeof revision === "number")
    return { label: `Revision ${revision}`, tone: "revision" };
  return readyForApproval
    ? { label: "In approval", tone: "routing" }
    : { label: "Draft", tone: "draft" };
};
